import React, { useState, useEffect, FC, useRef } from "react";
import { Icon } from "../../components/Component";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button } from "reactstrap";
import { Modal } from "antd";
import dayjs from "dayjs";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface MaterialHandoverPDFProps {
  handoverData: any;
  buyerOrgLogo?: string;
  getPdfUrl?: (blob: Blob) => void;
}

const MaterialHandoverPDF: FC<MaterialHandoverPDFProps> = ({
  handoverData,
  buyerOrgLogo,
  getPdfUrl,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;
    const boxX = margin;
    const boxY = 20;
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41;
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };

  const drawSignatureBox = (
    doc: jsPDF,
    startY: number,
    signatureImage: string | null,
    label: string = "Authorised Signature"
  ) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxWidth = 60;
    const boxHeight = 25;
    const x = pageWidth - boxWidth - 10;
    const y = startY + 10;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, boxWidth, boxHeight);

    if (signatureImage) {
      try {
        const imgWidth = 50;
        const imgHeight = 15;
        const imgX = x + (boxWidth - imgWidth) / 2;
        const imgY = y + 3;

        doc.addImage(signatureImage, "PNG", imgX, imgY, imgWidth, imgHeight);
      } catch (error) {
        console.error("Error adding signature image:", error);
      }
    }

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(label, x + boxWidth / 2, y + boxHeight - 3, {
      align: "center",
    });
  };

  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();
    const header = handoverData?.header || {};
    const lines = handoverData?.lines || [];
    const signature = handoverData?.signature || null;

    let signatureBase64: string | null = null;
    if (signature) {
      try {
        signatureBase64 = await fetchImageAsBase64(signature);
      } catch (error) {
        console.error("Error fetching signature:", error);
      }
    }

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;
          const height = Math.min(img.height, maxHeight);
          const width = height * aspectRatio;
          doc.addImage(img.src, "PNG", x, y, width, height);
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          reject(new Error("Image load failed"));
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10);
      } else {
        doc.setFontSize(20);
        doc.text("Procurex", 10, 20);
      }
    };

    const addTable = (doc: any, handoverData: any, currentY: any) => {
      const lines = handoverData?.lines || [];

      const columns = [
        { title: "Sno", dataKey: "sno" },
        { title: "Material Category", dataKey: "categoryName" },
        { title: "Material Name", dataKey: "materialName" },
        { title: "UOM", dataKey: "uomName" },
        { title: "Handovered Quantity", dataKey: "quantityHandovered" },
        { title: "Utilized Quantity", dataKey: "quantityUtilized" },
        { title: "Returned Quantity", dataKey: "quantityReturned" },
        { title: "Damaged Quantity", dataKey: "quantityDamaged" },
      ];

      const rows = lines.map((line: any, index: number) => ({
        sno: (index + 1).toString(),
        categoryName: line.categoryName || "-",
        materialName: line.materialName || "-",
        uomName: line.uomName || "-",
        quantityHandovered: Number(line.quantityHandovered || 0).toFixed(2),
        quantityUtilized: Number(line.quantityUtilized || 0).toFixed(2),
        quantityReturned: Number(line.quantityReturned || 0).toFixed(2),
        quantityDamaged: Number(line.quantityDamaged || 0).toFixed(2),
      }));

      const totalHandovered = lines.reduce(
        (sum: number, line: any) => sum + Number(line.quantityHandovered || 0),
        0
      ).toFixed(2);

      doc.autoTable({
        columns: columns,
        body: rows,
        startY: currentY,
        pageBreak: "auto",
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "linebreak",
          lineWidth: 0.1,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fontSize: 8,
          fillColor: [230, 230, 230],
          textColor: 0,
          valign: "middle",
          halign: "center",
          fontStyle: "bold",
        },
        footStyles: {
          fontSize: 8,
          textColor: 0,
          fillColor: false,
          lineWidth: 0,
          halign: "right",
        },
        bodyStyles: {
          valign: "middle",
          halign: "center",
        },
        theme: "grid",
        margin: { top: 5, bottom: 10, left: 5, right: 5 },
        didDrawPage: function (data: any) {
          const finalY = data.cursor.y;
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.text(
            `Total Handovered Quantity: ${totalHandovered}`,
            10,
            finalY + 10
          );
        },
      });

      const finalY = (doc as any).lastAutoTable?.finalY || currentY + 100;
      return finalY + 15;
    };

    const addFooter = async (
      doc: jsPDF,
      pageNum: number,
      totalPages: number
    ) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = handoverData?.orgName || "";
      const line2 = handoverData?.orgAddress || "";
      const line3 = handoverData?.orgCIN || "";
      const line4 = handoverData?.orgGSTIN || "";

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      doc.setFontSize(8);
      const pageText = `Page ${pageNum} of ${totalPages}`;
      doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });

      doc.setFontSize(7);
      if (line1) doc.text(line1, pageWidth / 2, startY, { align: "center" });
      if (line2) doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      if (line3) doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, { align: "center" });
      if (line4) doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, { align: "center" });
    };

    const addGeneralDetailsSection = async (doc: jsPDF, handoverData: any) => {
      const header = handoverData?.header || {};
      const sectionY = 16;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);

      let currentY = sectionY + 10;

      // Add Site details
      if (header) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(header.siteName || "", 10, currentY);
        currentY += 6;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");

        if (header.segmentLevel && header.segmentLevel !== "null") {
          doc.text(`Site Hierarchy: ${header.segmentLevel}`, 10, currentY);
          currentY += 5;
        }

        if (header.siteStructureUnitName) {
          const towerLabel = header.segmentLevel === "Flat" || header.segmentLevel === "Floor"
            ? "Tower / Block:"
            : "Unit:";
          doc.text(`${towerLabel} ${header.siteStructureUnitName}`, 10, currentY);
          currentY += 5;
        }

        if (header.floorNo) {
          doc.text(`Floor: ${header.floorNo}`, 10, currentY);
          currentY += 5;
        }

        if (header.siteStructureSubUnitName) {
          doc.text(`Flat: ${header.siteStructureSubUnitName}`, 10, currentY);
          currentY += 5;
        }

        if (header.locationName) {
          doc.text(`Location: ${header.locationName}`, 10, currentY);
          currentY += 5;
        }

        currentY += 5;
      }

      // Add "MATERIAL HANDOVER" text on the right
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const materialHandoverText = "MATERIAL HANDOVER";
      const textWidth = doc.getTextWidth(materialHandoverText);
      const rightX = pageWidth - textWidth - 10;
      doc.setTextColor(20, 20, 20);
      doc.text(materialHandoverText, rightX, sectionY + 30);

      // Draw horizontal line
      doc.setDrawColor(150, 150, 150);
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 10;

      // Add Handover details tables
      const sectionWidth = (pageWidth - 10) / 2;
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;
      const tableStartY = currentY;

      // Left table - Handover details
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          ["Handover Number:", header.handoverNumber || " "],
          ["Handover Date:", header.handoverDate ? dayjs(header.handoverDate).format("DD-MMM-YYYY") : " "],
          ["Utilization Status:", header.utilizationStatus || " "],
          ["Issue Number:", header.issueNumber || " "],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      // Right table - Creation details
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          ["Creation Date:", header.createdAt ? dayjs(header.createdAt).format("DD-MMM-YYYY") : " "],
          ["Created By:", header.createdUserName || " "],
          ["Handover To:", header.handoverToName || " "],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      const maxHeight = Math.max(leftTableHeight, rightTableHeight);
      doc.setDrawColor(150, 150, 150);
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5);

      return maxHeight + 10;
    };

    // Generate PDF
    let leftLogo: string | null = null;

    if (buyerOrgLogo) {
      try {
        leftLogo = await fetchImageAsBase64(buyerOrgLogo);
      } catch (error) {
        console.error("Buyer logo load failed", error);
        leftLogo = null;
      }
    }
    const currentY = await addGeneralDetailsSection(doc, handoverData);

    const tableEndY = await addTable(doc, handoverData, currentY);

    // Draw signature box after table
    drawSignatureBox(
      doc,
      tableEndY,
      signatureBase64,
      handoverData?.signatureLabel || "Authorised Signature"
    );

    const applyHeaderAndFooter = async () => {
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc, i, totalPages);
        drawBorderedBox(doc);
      }
    };

    await applyHeaderAndFooter();

    const finalPageCount = (doc as any).internal.getNumberOfPages();
    if (finalPageCount > 0) {
      doc.setPage(1);
    }

    if (download) {
      try {
        const pdfBlob = doc.output("blob");
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `MH_${header.handoverNumber || "MaterialHandover"}.pdf`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error during PDF download:", error);
        try {
          doc.save(`MH_${header.handoverNumber || "MaterialHandover"}.pdf`);
        } catch (saveError) {
          console.error("Error with doc.save():", saveError);
          alert("Failed to download PDF. Please try again.");
        }
      }
      return;
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return { pdfUrl, pdfBlob };
  };

  // Call generatePDF on mount to get the blob reference
  useEffect(() => {
    let isMounted = true;

    const initPDF = async () => {
      try {
        const result = await generatePDF();
        if (isMounted && result?.pdfBlob && getPdfUrl) {
          getPdfUrl(result.pdfBlob);
        }
      } catch (error) {
        console.log(error);
      }
    };

    initPDF();

    return () => {
      isMounted = false;
    };
  }, [handoverData]);

  const downloadPDF = async () => {
    try {
      await generatePDF(true);
    } catch (error) {
      console.error("Error in downloadPDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  return (
    <>
      <Button
        type="button"
        color=""
        onClick={async () => {
          const { pdfUrl: _pdfUrl }: any = await generatePDF();
          setPdfUrl(_pdfUrl);
        }}
      >
        <Icon name="file-pdf" style={{ marginRight: 1 }} />
        <span>PDF</span>
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Material Handover</span>
              <span
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadPDF().catch((error) => {
                    console.error("Download error:", error);
                  });
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            referrerPolicy="no-referrer"
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default MaterialHandoverPDF;